import { getCMSConfig } from '../cms';
import { INotificationProperties } from '@vfde-brix/ws10/notification';
import { AppError } from '../../container/App/interface';
import { ErrorType } from '../../container/App/enum';
import {
    createNE4NotificationText,
    formatDownload,
    formatProductName,
} from '../formatter';
import { NotificationType } from '../../enum';
import { NOTIFICATION_BUTTON_LINK } from '../../constants';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import {
    ButtonLinkColorVariant,
    OptIconPosition,
} from '@vfde-brix/ws10/button-link';
import {
    JarvisMarketability,
    JarvisProduct,
} from '../../interface';
import { sortTariffsByDownloadSpeed } from '../../container/App/util/reducer.util';

/**
 * Returns props to update for LM success notification
 */
export const getSuccessNotificationProps = (): Partial<INotificationProperties> => {
    const successNotification = getCMSConfig().lm.notifications.success;

    return {
        stdHeadline: successNotification.headline,
        txtContent: successNotification.content,
    };
};

/**
 * Returns props to update for LM error notification
 */
export const getErrorNotificationProps = (appError: AppError): Partial<INotificationProperties> => {
    const notifications = getCMSConfig().lm.notifications;

    const notification = appError.type === ErrorType.LM_VALIDATION_ERROR
        ? notifications.validationError
        : notifications.backendError;

    return {
        stdHeadline: notification.headline,
        txtContent: notification.content,
    };
};

/**
 * Returns props to update for AVC result notification
 */
export const getAvcResultNotificationProps = (
    preSelectedTariff: JarvisProduct | undefined,
    isSelectedAvailable: boolean,
    availableTariffs: JarvisProduct[],
    marketability: JarvisMarketability | undefined,
): Partial<INotificationProperties> => {
    const { available, slowerAvailable, notAvailable } = getCMSConfig().avcResult.cable;

    const fastest = sortTariffsByDownloadSpeed(availableTariffs)[0];
    const ne4Text = createNE4NotificationText(fastest?.minorityNE4Discount?.price.value);

    // the selected KIP tariff or a higher one is available and any TV Connect product.
    if (isSelectedAvailable && preSelectedTariff && fastest) {
        return {
            optState: NotificationType.SUCCESS,
            stdHeadline: formatProductName(available.headline, preSelectedTariff.name),
            txtContent: formatDownload(available.text, fastest.download) + ne4Text,
            containerButtons: undefined,
        };
    }

    // KIP is available. But a lower tariff than the selected one.
    if (!isSelectedAvailable && preSelectedTariff && fastest) {
        return {
            optState: NotificationType.INFO,
            stdHeadline: formatProductName(slowerAvailable.headline, preSelectedTariff.name),
            txtContent: formatDownload(slowerAvailable.text, fastest.download) + ne4Text,
            containerButtons: undefined,
        };
    }

    // KIP is not available but Kabel Connection (=ÜP2) is possible.
    if (marketability?.isCableConnection) {
        return {
            optState: NotificationType.WARN,
            stdHeadline: notAvailable.cableConnection.headline,
            txtContent: notAvailable.cableConnection.text,
            containerButtons: [{
                type: NOTIFICATION_BUTTON_LINK,
                optColorVariant: ButtonLinkColorVariant.Primary200,
                stdLabel: notAvailable.cableConnection.buttonLink.label,
                stdSystemIconName: notAvailable.cableConnection.buttonLink.icon,
                linkHref: notAvailable.cableConnection.buttonLink.linkHref,
                optIconPosition: OptIconPosition.RIGHT,
                business: NO_PATTERN_BUSINESS_LOGIC,
            }],
        };
    }

    // KIP is not available but Fiber is available, then Fiber is offered.
    // KIP & Fiber are not available but DSL is available, then DSL is offered.
    // KIP, Fiber & DSL are not available, Gigacube is offered.
    return {
        optState: NotificationType.WARN,
        stdHeadline: notAvailable.headline,
        txtContent: '',
        containerButtons: undefined,
    };
};
